/**
 * The cookie banner's theme: how Termly's banner and preference modal are colored on a
 * storefront, resolved and sanitized into CSS custom properties the way the page theme is
 * (`storefrontThemeVars.ts`).
 *
 * Every token has a store token it falls back to, so a storefront that sets nothing keeps
 * following its own palette: the unset value is the live reference `var(--card)`, never a hex,
 * and a tenant who recolors the store later never has to touch the banner. The fallback is a
 * code-owned constant; tenant input goes through `safeThemeColor`, whose functions admit only
 * color characters as arguments, so `var(...)`, `url(...)` and any call nested in a color
 * (`rgb(var(--x))`) are refused, and the loop walks `COOKIE_BANNER_TOKEN_KEYS`, never the stored
 * object, so a tenant can supply a value for one of our variables but never introduce a name.
 */
import { z } from "zod";

import { safeThemeColor, safeThemeRadius } from "./storefrontThemeVars.js";
import type { StorefrontTokenKey } from "./storefrontTokens.js";
import { refuseUnacceptedThemeColors } from "./themeColorInput.js";

/**
 * The banner tokens, in the order a settings editor shows them. `fallback` names the store token
 * the banner inherits while the token is unset; `label` is the editor's wording.
 */
export const COOKIE_BANNER_TOKENS = [
  { key: "background", label: "Background", fallback: "card" },
  { key: "foreground", label: "Text", fallback: "card-foreground" },
  { key: "button", label: "Button", fallback: "primary" },
  { key: "button-foreground", label: "Button text", fallback: "primary-foreground" },
  { key: "border", label: "Border", fallback: "border" },
] as const satisfies readonly { key: string; label: string; fallback: StorefrontTokenKey }[];

export type CookieBannerTokenKey = (typeof COOKIE_BANNER_TOKENS)[number]["key"];

export const COOKIE_BANNER_TOKEN_KEYS = COOKIE_BANNER_TOKENS.map(
  (token) => token.key,
) as CookieBannerTokenKey[];

/**
 * A stored banner theme: a partial token map plus an optional corner radius, like the page
 * theme's `tokens` and `radius`. Font is never a token: the banner inherits the page font.
 */
export const cookieBannerThemeSchema = z.object({
  tokens: z.partialRecord(z.enum(COOKIE_BANNER_TOKEN_KEYS), z.string()).optional(),
  radius: z.string().optional(),
});
export type CookieBannerTheme = z.infer<typeof cookieBannerThemeSchema>;

/**
 * A banner theme as `storefront.setTermlyTheme` and `platform.termly.setTheme` take it: the stored
 * shape, refusing a color the resolver would drop and naming its token. Writers only: stored rows
 * are read through `cookieBannerThemeSchema`, so one saved before this check still parses.
 */
export const cookieBannerThemeInputSchema = cookieBannerThemeSchema.superRefine(
  refuseUnacceptedThemeColors,
);

/** The variable each token writes, `--cookie-banner-<key>`. */
export type CookieBannerVar = `--cookie-banner-${CookieBannerTokenKey}`;

export interface CookieBannerThemeVars {
  /** Every banner token, keyed `--cookie-banner-<key>`; unset ones are `var(--<store token>)`. */
  colors: Record<CookieBannerVar, string>;
  /** `--cookie-banner-radius`, the banner's and the modal's corners: the sanitized radius, or the store's. */
  radius: string;
  /** `--cookie-banner-button-radius`: two pixels inside a set radius, or the store's control radius. */
  buttonRadius: string;
}

/**
 * The store's own corners, by reference, for a banner theme with no radius: the surface at the
 * design system's `xl` tier, as its toasts are, and the buttons at its control radius, so a theme
 * with pill buttons gets pill buttons on the banner too. Written from the variables the page theme
 * sets on <html> (`--theme-radius-scale`, `--theme-radius-control`), never from the `--radius-*`
 * tiers, which the storefront's Tailwind build emits only where a class uses one.
 */
const STORE_SURFACE_RADIUS = "calc(20px * var(--theme-radius-scale, 1))";
const STORE_BUTTON_RADIUS =
  "var(--theme-radius-control, calc(12px * var(--theme-radius-scale, 1)))";

/**
 * The complete, sanitized variable set for a stored banner theme. Always complete: an unset or
 * rejected token resolves to its store token by reference, so the banner never half-inherits and
 * never goes blank.
 */
export function resolveCookieBannerThemeVars(
  theme?: CookieBannerTheme | null,
): CookieBannerThemeVars {
  const stored = theme?.tokens ?? {};
  const colors = {} as Record<CookieBannerVar, string>;
  for (const token of COOKIE_BANNER_TOKENS) {
    colors[`--cookie-banner-${token.key}`] =
      safeThemeColor(stored[token.key]) ?? `var(--${token.fallback})`;
  }
  const radius = safeThemeRadius(theme?.radius);
  if (!radius) {
    return { colors, radius: STORE_SURFACE_RADIUS, buttonRadius: STORE_BUTTON_RADIUS };
  }
  // A set radius always carries a unit (`safeThemeRadius`), so it subtracts inside calc().
  return { colors, radius, buttonRadius: `max(0px, calc(${radius} - 2px))` };
}
